// ─────────────────────────────────────────────────────────────────────────────
// The shell: header with the file's identity and the actions the renderer can
// honour, the renderer itself, a details panel, a status bar, and the strip
// for walking a collection. It owns loading, detection, keyboard handling,
// fullscreen, focus and announcements; the renderers own the picture.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Portal } from '../core/Portal/Portal.js';
import { Button } from '../core/Button/Button.js';
import { Badge } from '../core/Badge/Badge.js';
import { Kbd } from '../core/Kbd/Kbd.js';
import { Progress } from '../core/Progress/Progress.js';
import { ActionIcon } from '../core/ActionIcon/ActionIcon.js';
import { Menu } from '../overlays/Menu/Menu.js';
import { ArrowLeft, ChevronLeft, ChevronRight, Close, Copy, Download, DotsVertical, ExternalLink } from '../icons/builtin.js';
import { trapFocus, lockBodyScroll, unlockBodyScroll } from '@repletoryreach/sdk-utils/dom';
import { createAnnouncer, type Announcer } from '@repletoryreach/sdk-utils/a11y';
import { copyText } from '@repletoryreach/sdk-utils/clipboard';
import { downloadBlob } from '@repletoryreach/sdk-utils/file';
import { useMediaQuery, useReducedMotion } from '@repletoryreach/sdk-utils/hooks';
import { IconButton, familyLabel } from './chrome.js';
import { FamilyIcon, IconExitFullscreen, IconFullscreen, IconKeyboard, IconLink, IconPanel, IconPrint, IconRefresh, IconShare, IconWarning } from './icons.js';
import { detect } from './detect.js';
import { DEFAULT_LABELS, fill, type FileViewerLabels } from './labels.js';
import { DEFAULT_MAX_LOCAL_BYTES, DEFAULT_MAX_REMOTE_BYTES, displayName, fetchWhole, loadSource, revoke, toViewerError } from './load.js';
import { formatBytes, sha256Hex } from './format.js';
import { BUILTIN_RENDERERS } from './renderers/index.js';
import { FileSourcePicker } from './FileSourcePicker.js';
import type { DetectedFile, FileDetailRow, FileViewerError, FileViewerProps, FileViewerSource, LoadedFile, RendererRegistration } from './types.js';
import './FileViewer.css';

type Phase =
  | { kind: 'idle' }
  | { kind: 'inspecting' }
  | { kind: 'loading'; loaded: number; total: number | null }
  | { kind: 'processing' }
  | { kind: 'ready'; file: LoadedFile; detected: DetectedFile }
  | { kind: 'error'; error: FileViewerError; name: string };

const AUTO_CHECKSUM_BYTES = 32 * 1024 * 1024;
const RETYPED = new Set(['image', 'pdf', 'video', 'audio']);

export function FileViewer(props: FileViewerProps): ReactNode {
  if (!props.opened) return null;
  return (
    <Portal target={props.portalTarget}>
      <FileViewerFrame {...props} />
    </Portal>
  );
}

/**
 * A browser hands back a blob typed by the response, and a response that said
 * `application/octet-stream` about a PNG leaves an <img> with nothing to draw.
 * Once the bytes have said what they are, the blob is re-wrapped in that type.
 */
function retype(file: LoadedFile, detected: DetectedFile): LoadedFile {
  if (!file.blob || !detected.mime || !RETYPED.has(detected.family)) return file;
  if (file.blob.type === detected.mime) return file;
  revoke(file);
  const blob = new Blob([file.blob], { type: detected.mime });
  return { ...file, blob, mime: detected.mime, objectUrl: URL.createObjectURL(blob) };
}

function isTyping(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.closest !== 'function') return false;
  return !!el.closest('input, textarea, select, [contenteditable="true"]');
}

function FileViewerFrame({
  onClose, source, collection, initialIndex, labels: labelOverrides, renderers, loaders,
  allowDownload = true, allowShare = true, allowOpenExternal = true, allowPrint = true,
  maxLocalBytes = DEFAULT_MAX_LOCAL_BYTES, maxRemoteBytes = DEFAULT_MAX_REMOTE_BYTES,
  withSourcePicker = true, onError, onTelemetry, zIndex, className
}: FileViewerProps): ReactNode {
  const labels = useMemo<FileViewerLabels>(() => ({ ...DEFAULT_LABELS, ...labelOverrides }), [labelOverrides]);
  const reducedMotion = useReducedMotion();
  const wide = useMediaQuery('(min-width: 1024px)');

  // The list to walk: the collection, with the opening source first when it is not already in it.
  const [items, setItems] = useState<FileViewerSource[]>(() => {
    const list = collection && collection.length > 0 ? [...collection] : [];
    if (source && !list.includes(source)) list.unshift(source);
    return list;
  });
  const [index, setIndex] = useState<number>(() => {
    if (initialIndex != null) return Math.max(0, Math.min(initialIndex, Math.max(0, items.length - 1)));
    return source ? Math.max(0, items.indexOf(source)) : 0;
  });
  const current = items[index];

  const [phase, setPhase] = useState<Phase>({ kind: 'idle' });
  const [reg, setReg] = useState<RendererRegistration>({});
  const [detailsOpen, setDetailsOpen] = useState<boolean>(() => typeof window !== 'undefined' && window.innerWidth >= 1280);
  const [fullscreen, setFullscreen] = useState(false);
  const [help, setHelp] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [checksum, setChecksum] = useState<{ state: 'idle' | 'busy' | 'done'; value?: string }>({ state: 'idle' });
  const [reload, setReload] = useState<{ tick: number; revalidate: boolean }>({ tick: 0, revalidate: false });

  const root = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<LoadedFile | null>(null);
  const announcer = useRef<Announcer | null>(null);
  const openedAt = useRef<number>(typeof performance !== 'undefined' ? performance.now() : 0);
  const previous = useRef<{ etag: string | null | undefined; size: number } | null>(null);

  const announce = useCallback((message: string) => { announcer.current?.announce(message); }, []);
  const register = useCallback((r: RendererRegistration) => { setReg(r); }, []);

  // Body lock, focus trap, focus restore, the live region.
  useEffect(() => {
    const opener = (document.activeElement as HTMLElement | null) ?? null;
    lockBodyScroll();
    announcer.current = createAnnouncer();
    const cleanupTrap = root.current ? trapFocus(root.current) : () => {};
    root.current?.focus();
    onTelemetry?.({ type: 'open' });
    return () => {
      cleanupTrap();
      unlockBodyScroll();
      announcer.current?.destroy();
      announcer.current = null;
      opener?.focus?.();
      onTelemetry?.({ type: 'close' });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fullscreen tracks the document, because Escape leaves fullscreen before it reaches us.
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  // Load the current file. Aborts and revokes on change or close.
  useEffect(() => {
    if (!current) { setPhase({ kind: 'idle' }); return; }
    const ac = new AbortController();
    const started = typeof performance !== 'undefined' ? performance.now() : 0;
    const revalidate = reload.revalidate;
    setPhase({ kind: 'inspecting' });
    setReg({});
    setChecksum({ state: 'idle' });
    loadSource(current, {
      signal: ac.signal, maxLocalBytes, maxRemoteBytes, revalidate,
      onProgress: (loaded, total) => setPhase({ kind: 'loading', loaded, total })
    }).then((loaded) => {
      if (ac.signal.aborted) { revoke(loaded); return; }
      setPhase({ kind: 'processing' });
      const detected = detect({
        name: loaded.name,
        mime: loaded.mime || (current.kind === 'remote' ? current.mime : undefined),
        size: loaded.size,
        head: loaded.head
      });
      const file = retype(loaded, detected);
      fileRef.current = file;
      setPhase({ kind: 'ready', file, detected });
      onTelemetry?.({ type: 'loaded', family: detected.family, origin: file.origin, ms: Math.round((typeof performance !== 'undefined' ? performance.now() : 0) - started) });
      if (revalidate && previous.current) {
        const changed = (file.etag && previous.current.etag && file.etag !== previous.current.etag) || file.size !== previous.current.size;
        announce(changed ? labels.refreshedNewer : labels.refreshed);
      } else {
        announce(`${labels.loadedState}: ${current.title ?? file.name}`);
      }
      previous.current = { etag: file.etag, size: file.size };
    }).catch((e: unknown) => {
      if (ac.signal.aborted) return;
      const error = toViewerError(e);
      setPhase({ kind: 'error', error, name: displayName(current) });
      onError?.(error);
      onTelemetry?.({ type: 'error', error });
      announce(`${labels.errorState}: ${errorMessage(labels, error, maxRemoteBytes, current)}`);
    });
    return () => {
      ac.abort();
      revoke(fileRef.current);
      fileRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current, reload.tick]);

  const ready = phase.kind === 'ready' ? phase : null;
  const file = ready?.file ?? null;
  const detected = ready?.detected ?? null;
  const family = detected?.family ?? (current ? undefined : undefined);
  const title = current?.title ?? (file?.name || (current ? displayName(current) : labels.viewerTitle));
  const link = file?.link;

  const renderer = useMemo(() => {
    if (!detected) return null;
    return [...(renderers ?? []), ...BUILTIN_RENDERERS].find((r) => r.match(detected)) ?? null;
  }, [detected, renderers]);

  // ── Actions ─────────────────────────────────────────────────────────────
  const download = useCallback(async () => {
    if (!file) return;
    onTelemetry?.({ type: 'action', action: 'download' });
    if (file.blob) { downloadBlob(file.blob, file.name); return; }
    if (file.streamUrl) {
      setDownloading(true);
      try {
        downloadBlob(await fetchWhole(file.streamUrl), file.name);
      } catch {
        announce(labels.errorNetwork);
      } finally {
        setDownloading(false);
      }
    }
  }, [file, announce, labels.errorNetwork, onTelemetry]);

  const openExternally = useMemo(() => {
    if (!allowOpenExternal || !file) return null;
    const target = link ?? file.objectUrl ?? file.streamUrl;
    if (!target) return null;
    return () => { onTelemetry?.({ type: 'action', action: 'open_external' }); window.open(target, '_blank', 'noopener'); };
  }, [allowOpenExternal, file, link, onTelemetry]);

  const copy = useCallback(async (value: string) => {
    const r = await copyText(value);
    announce(r.kind === 'ok' ? labels.copied : labels.copyFailed);
  }, [announce, labels.copied, labels.copyFailed]);

  const canShare = allowShare && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && !!file
    && (!!link || (!!file.blob && typeof navigator.canShare === 'function' && navigator.canShare({ files: [new File([file.blob], file.name, { type: file.mime })] })));
  const share = useCallback(async () => {
    if (!file) return;
    onTelemetry?.({ type: 'action', action: 'share' });
    try {
      if (link) await navigator.share({ title, url: link });
      else if (file.blob) await navigator.share({ title, files: [new File([file.blob], file.name, { type: file.mime })] });
    } catch { /* the user dismissed the sheet */ }
  }, [file, link, title, onTelemetry]);

  const canPrint = allowPrint && !!reg.capabilities?.print && !!reg.actions?.print;
  const canRefresh = current?.kind === 'remote' && !!file && !file.streamUrl;
  const refresh = useCallback(() => setReload((r) => ({ tick: r.tick + 1, revalidate: true })), []);
  const retry = useCallback(() => setReload((r) => ({ tick: r.tick + 1, revalidate: false })), []);

  const toggleFullscreen = useCallback(() => {
    const el = root.current;
    if (!el) return;
    if (document.fullscreenElement) void document.exitFullscreen?.();
    else void el.requestFullscreen?.();
  }, []);

  const step = useCallback((delta: number) => {
    setIndex((i) => {
      const next = i + delta;
      if (next < 0 || next >= items.length) return i;
      return next;
    });
  }, [items.length]);

  // ── Keyboard ────────────────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return;
      if (e.key === 'Escape') {
        if (help) { setHelp(false); e.preventDefault(); return; }
        if (document.fullscreenElement) return; // the browser leaves fullscreen on this Escape
        e.preventDefault();
        onClose();
        return;
      }
      if (isTyping(e.target)) return;
      if (reg.onKey?.(e)) { e.preventDefault(); return; }
      switch (e.key) {
        case 'ArrowLeft': case 'PageUp': if (items.length > 1) { e.preventDefault(); step(-1); } break;
        case 'ArrowRight': case 'PageDown': if (items.length > 1) { e.preventDefault(); step(1); } break;
        case 'f': case 'F': e.preventDefault(); toggleFullscreen(); break;
        case 'i': case 'I': e.preventDefault(); setDetailsOpen((v) => !v); break;
        case '?': e.preventDefault(); setHelp((v) => !v); break;
        default: break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [reg, items.length, step, toggleFullscreen, onClose, help]);

  // ── Checksum: automatic for a modest file with the panel open, on demand otherwise ──
  useEffect(() => {
    if (!detailsOpen || !file?.blob || checksum.state !== 'idle' || file.size > AUTO_CHECKSUM_BYTES) return;
    let cancelled = false;
    setChecksum({ state: 'busy' });
    sha256Hex(file.blob).then((value) => { if (!cancelled) setChecksum({ state: 'done', value }); }).catch(() => { if (!cancelled) setChecksum({ state: 'idle' }); });
    return () => { cancelled = true; };
  }, [detailsOpen, file, checksum.state]);

  const computeChecksum = useCallback(() => {
    if (!file?.blob) return;
    setChecksum({ state: 'busy' });
    sha256Hex(file.blob).then((value) => setChecksum({ state: 'done', value })).catch(() => setChecksum({ state: 'idle' }));
  }, [file]);

  // ── Derived chrome ──────────────────────────────────────────────────────
  const stateBadge = (() => {
    switch (phase.kind) {
      case 'ready': return { text: labels.loadedState, tone: 'success' };
      case 'error': return { text: labels.errorState, tone: 'error' };
      case 'loading': return { text: labels.loadingState, tone: 'info' };
      case 'processing': return { text: labels.processingState, tone: 'info' };
      case 'inspecting': return { text: labels.inspectingState, tone: 'info' };
      default: return null;
    }
  })();

  const originLabel = current ? (current.kind === 'local' ? labels.local : labels.remote) : '';
  const size = file?.size || (current?.kind === 'remote' ? current.size : current?.kind === 'local' ? current.file.size : current?.kind === 'blob' ? current.blob.size : 0);

  const detailRows = useMemo<FileDetailRow[]>(() => {
    if (!file || !detected) return [];
    const rows: FileDetailRow[] = [
      { label: labels.detailFilename, value: file.name, copy: file.name },
      ...(current?.title && current.title !== file.name ? [{ label: labels.detailTitle, value: current.title }] : []),
      { label: labels.detailType, value: familyLabel(labels, detected.family) },
      { label: labels.detailMime, value: detected.mime || labels.unknownValue, copy: detected.mime || undefined },
      { label: labels.detailSize, value: formatBytes(file.size) || labels.unknownValue },
      { label: labels.detailSource, value: originLabel },
      ...(file.host ? [{ label: labels.detailHost, value: file.host }] : []),
      ...(link ? [{ label: labels.copyLink, value: <span className="rr-fv__mono rr-fv__break">{link}</span>, copy: link }] : []),
      ...(file.lastModified ? [{ label: labels.detailModified, value: formatWhen(file.lastModified) }] : []),
      ...(file.etag ? [{ label: labels.detailEtag, value: <span className="rr-fv__mono">{file.etag}</span>, copy: file.etag }] : []),
      ...(current?.details ?? []),
      ...(reg.details ?? [])
    ];
    return rows;
  }, [file, detected, labels, current, originLabel, link, reg.details]);

  const errorText = phase.kind === 'error' && current ? errorMessage(labels, phase.error, maxRemoteBytes, current) : '';

  const showPicker = !current && withSourcePicker;

  return (
    <div
      ref={root}
      className={['rr-fv', fullscreen && 'rr-fv--fullscreen', detailsOpen && 'rr-fv--details', !wide && 'rr-fv--narrow', className].filter(Boolean).join(' ')}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      tabIndex={-1}
      style={zIndex != null ? { zIndex } : undefined}
      data-family={detected?.family}
    >
      <header className="rr-fv__header">
        <IconButton label={labels.back} onClick={onClose}><ArrowLeft size={16} /></IconButton>
        <div className={`rr-fv__tile rr-fv__tile--${detected?.family ?? 'unknown'}`} aria-hidden>
          <FamilyIcon family={detected?.family ?? 'unknown'} size={18} />
        </div>
        <div className="rr-fv__heading">
          <div className="rr-fv__name" title={title}>{title}</div>
          <div className="rr-fv__meta">
            {size ? <span>{formatBytes(size)}</span> : null}
            {current && <Badge size="xs" variant="light">{originLabel}</Badge>}
            {detected && <Badge size="xs" variant="outline">{familyLabel(labels, detected.family)}</Badge>}
            {stateBadge && <Badge size="xs" variant="dot" color={stateBadge.tone}>{stateBadge.text}</Badge>}
            {file?.host && <span className="rr-fv__host">{file.host}</span>}
          </div>
        </div>
        <div className="rr-fv__actions">
          {link && (
            <Button size="sm" variant="default" leftSection={<IconLink />} onClick={() => void copy(link)} className="rr-fv__action">
              <span className="rr-fv__action-label">{labels.copyLink}</span>
            </Button>
          )}
          {canShare && (
            <Button size="sm" variant="default" leftSection={<IconShare />} onClick={() => void share()} className="rr-fv__action">
              <span className="rr-fv__action-label">{labels.share}</span>
            </Button>
          )}
          {allowDownload && file && (file.blob || file.streamUrl) && (
            <Button size="sm" variant="default" leftSection={<Download size={16} />} loading={downloading} onClick={() => void download()} className="rr-fv__action rr-fv__action--download">
              <span className="rr-fv__action-label">{labels.download}</span>
            </Button>
          )}
          {canPrint && (
            <Button size="sm" variant="default" leftSection={<IconPrint />} onClick={() => { onTelemetry?.({ type: 'action', action: 'print' }); reg.actions?.print?.(); }} className="rr-fv__action">
              <span className="rr-fv__action-label">{labels.print}</span>
            </Button>
          )}
          {openExternally && (
            <Button size="sm" variant="default" leftSection={<ExternalLink size={16} />} onClick={openExternally} className="rr-fv__action">
              <span className="rr-fv__action-label">{labels.openExternally}</span>
            </Button>
          )}
          <Menu position="bottom-end" width={240}>
            <Menu.Target>
              <ActionIcon type="button" variant="subtle" size="md" aria-label={labels.more} className="rr-fv__iconbtn"><DotsVertical size={16} /></ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              {file && <Menu.Item leftSection={<Copy size={14} />} onClick={() => void copy(file.name)}>{labels.copyName}</Menu.Item>}
              {link && <Menu.Item leftSection={<IconLink size={14} />} onClick={() => void copy(link)}>{labels.copyLink}</Menu.Item>}
              {canShare && <Menu.Item leftSection={<IconShare size={14} />} onClick={() => void share()}>{labels.share}</Menu.Item>}
              {allowDownload && file && (file.blob || file.streamUrl) && <Menu.Item leftSection={<Download size={14} />} onClick={() => void download()}>{labels.download}</Menu.Item>}
              {canPrint && <Menu.Item leftSection={<IconPrint size={14} />} onClick={() => reg.actions?.print?.()}>{labels.print}</Menu.Item>}
              {openExternally && <Menu.Item leftSection={<ExternalLink size={14} />} onClick={openExternally}>{labels.openExternally}</Menu.Item>}
              {canRefresh && <Menu.Item leftSection={<IconRefresh size={14} />} onClick={refresh}>{labels.refresh}</Menu.Item>}
              {reg.capabilities?.copyImage && reg.actions?.copyImage && (
                <Menu.Item leftSection={<Copy size={14} />} onClick={() => void reg.actions?.copyImage?.().then((ok) => announce(ok ? labels.copied : labels.copyFailed))}>{labels.copyImage}</Menu.Item>
              )}
              {reg.capabilities?.exportPreview && reg.actions?.exportPreview && (
                <Menu.Item leftSection={<Download size={14} />} onClick={() => reg.actions?.exportPreview?.()}>{detected?.family === 'video' ? labels.captureFrame : labels.exportPng}</Menu.Item>
              )}
              <Menu.Divider />
              <Menu.Item leftSection={<IconPanel size={14} />} onClick={() => setDetailsOpen((v) => !v)}>{detailsOpen ? labels.hideDetails : labels.details}</Menu.Item>
              <Menu.Item leftSection={<IconKeyboard size={14} />} onClick={() => setHelp(true)}>{labels.keyboardHelp}</Menu.Item>
            </Menu.Dropdown>
          </Menu>
          <IconButton label={detailsOpen ? labels.hideDetails : labels.details} pressed={detailsOpen} active={detailsOpen} onClick={() => setDetailsOpen((v) => !v)}><IconPanel /></IconButton>
          <IconButton label={fullscreen ? labels.exitFullscreen : labels.fullscreen} onClick={toggleFullscreen}>{fullscreen ? <IconExitFullscreen /> : <IconFullscreen />}</IconButton>
          <IconButton label={labels.close} onClick={onClose}><Close size={16} /></IconButton>
        </div>
      </header>

      <div className="rr-fv__body">
        <main className="rr-fv__main">
          {detected?.mismatch && (
            <div className="rr-fv__banner rr-fv__banner--warning" role="status"><IconWarning size={14} /> {labels.mismatchWarning}</div>
          )}

          {showPicker && (
            <FileSourcePicker
              labels={labels}
              maxLocalBytes={maxLocalBytes}
              onPick={(picked) => { setItems([picked]); setIndex(0); }}
            />
          )}

          {(phase.kind === 'inspecting' || phase.kind === 'loading' || phase.kind === 'processing') && (
            <div className="rr-fv__loading" role="status">
              <div className="rr-fv__loading-title">
                {phase.kind === 'loading' ? labels.loadingState : phase.kind === 'processing' ? labels.processingState : labels.inspectingState}
              </div>
              {phase.kind === 'loading' && (
                <div className="rr-fv__loading-bar">
                  {phase.total
                    ? <Progress value={Math.min(100, Math.round((phase.loaded / phase.total) * 100))} size="sm" />
                    : <Progress value={100} size="sm" animated striped />}
                  <div className="rr-fv__loading-text">
                    {phase.total ? fill(labels.progressOf, { loaded: formatBytes(phase.loaded), total: formatBytes(phase.total) }) : fill(labels.progressLoaded, { loaded: formatBytes(phase.loaded) })}
                  </div>
                </div>
              )}
            </div>
          )}

          {phase.kind === 'error' && current && (
            <div className="rr-fv__error" role="alert">
              <div className="rr-fv__tile rr-fv__tile--unknown rr-fv__tile--big"><IconWarning size={30} /></div>
              <div className="rr-fv__error-title">{labels.errorState}</div>
              <div className="rr-fv__error-message">{errorText}</div>
              <div className="rr-fv__notice-actions">
                {phase.error.code !== 'too_large' && <Button size="sm" variant="filled" onClick={retry}>{labels.retry}</Button>}
                {current.kind === 'remote' && current.linkUrl !== null && (
                  <>
                    <Button size="sm" variant="default" onClick={() => void copy(current.linkUrl ?? current.url)}>{labels.copyLink}</Button>
                    {allowOpenExternal && <Button size="sm" variant="default" onClick={() => window.open(current.linkUrl ?? current.url, '_blank', 'noopener')}>{labels.openExternally}</Button>}
                  </>
                )}
              </div>
            </div>
          )}

          {ready && renderer && (
            <renderer.component
              key={`${index}:${reload.tick}`}
              file={ready.file}
              detected={ready.detected}
              source={current!}
              labels={labels}
              loaders={loaders}
              register={register}
              announce={announce}
              download={() => void download()}
              openExternally={openExternally}
              reducedMotion={reducedMotion}
              fullscreen={fullscreen}
            />
          )}
        </main>

        {detailsOpen && (
          <aside className="rr-fv__aside" aria-label={labels.details}>
            <div className="rr-fv__aside-head">
              <span>{labels.details}</span>
              <ActionIcon type="button" variant="subtle" size="sm" aria-label={labels.hideDetails} onClick={() => setDetailsOpen(false)}><Close size={14} /></ActionIcon>
            </div>
            {detailRows.length === 0 ? (
              <div className="rr-fv__aside-empty">{current ? (phase.kind === 'error' ? errorText : labels.loadingState) : labels.pickerTitle}</div>
            ) : (
              <dl className="rr-fv__dl">
                {detailRows.map((row, i) => (
                  <div className="rr-fv__dl-row" key={`${row.label}-${i}`}>
                    <dt>{row.label}</dt>
                    <dd>
                      <span className="rr-fv__dd-value">{row.value}</span>
                      {row.copy && (
                        <ActionIcon type="button" variant="subtle" size="xs" aria-label={`${labels.copyName}: ${row.label}`} onClick={() => void copy(row.copy!)}><Copy size={12} /></ActionIcon>
                      )}
                    </dd>
                  </div>
                ))}
                {file?.blob && (
                  <div className="rr-fv__dl-row">
                    <dt>{labels.detailChecksum}</dt>
                    <dd>
                      {checksum.state === 'done' && checksum.value
                        ? <><span className="rr-fv__mono rr-fv__break rr-fv__dd-value">{checksum.value}</span><ActionIcon type="button" variant="subtle" size="xs" aria-label={labels.detailChecksum} onClick={() => void copy(checksum.value!)}><Copy size={12} /></ActionIcon></>
                        : checksum.state === 'busy'
                          ? <span className="rr-fv__dd-value">{labels.computingChecksum}</span>
                          : <Button size="xs" variant="default" onClick={computeChecksum}>{labels.computeChecksum}</Button>}
                    </dd>
                  </div>
                )}
              </dl>
            )}
          </aside>
        )}
      </div>

      <footer className="rr-fv__footer">
        <div className="rr-fv__status">{reg.status ?? stateBadge?.text ?? ''}</div>
        <div className="rr-fv__footer-mid">{detected && !detected.mismatch && detected.sniffedMime && detected.sniffedMime !== file?.mime ? '' : ''}</div>
        <div className="rr-fv__position">
          {items.length > 1 && (
            <>
              <IconButton label={labels.previous} size="sm" disabled={index === 0} onClick={() => step(-1)}><ChevronLeft size={14} /></IconButton>
              <span>{fill(labels.position, { index: index + 1, count: items.length })}</span>
              <IconButton label={labels.next} size="sm" disabled={index >= items.length - 1} onClick={() => step(1)}><ChevronRight size={14} /></IconButton>
            </>
          )}
        </div>
      </footer>

      {items.length > 1 && (
        <div className="rr-fv__strip" role="list">
          {items.map((item, i) => {
            const name = item.title ?? displayName(item);
            const fam = detectFamilyOf(item);
            return (
              <button
                type="button"
                key={item.id ?? `${i}-${name}`}
                role="listitem"
                className={['rr-fv__strip-item', i === index && 'rr-fv__strip-item--current'].filter(Boolean).join(' ')}
                aria-current={i === index ? 'true' : undefined}
                onClick={() => setIndex(i)}
                title={name}
              >
                <span className="rr-fv__strip-thumb">
                  {item.thumbnail ? <img src={item.thumbnail} alt="" /> : <span className={`rr-fv__tile rr-fv__tile--${fam}`}><FamilyIcon family={fam} size={16} /></span>}
                </span>
                <span className="rr-fv__strip-index">{i + 1}</span>
                <span className="rr-fv__strip-name">{name}</span>
              </button>
            );
          })}
        </div>
      )}

      {help && (
        <div className="rr-fv__help" role="dialog" aria-label={labels.helpTitle}>
          <div className="rr-fv__help-head">
            <span>{labels.helpTitle}</span>
            <ActionIcon type="button" variant="subtle" size="sm" aria-label={labels.close} onClick={() => setHelp(false)}><Close size={14} /></ActionIcon>
          </div>
          <dl className="rr-fv__help-list">
            <div><dt><Kbd>Esc</Kbd></dt><dd>{labels.helpEscape}</dd></div>
            <div><dt><Kbd>←</Kbd> <Kbd>→</Kbd></dt><dd>{labels.helpArrows}</dd></div>
            <div><dt><Kbd>Space</Kbd></dt><dd>{labels.helpSpace}</dd></div>
            <div><dt><Kbd>←</Kbd> <Kbd>→</Kbd></dt><dd>{labels.helpSeek}</dd></div>
            <div><dt><Kbd>+</Kbd> <Kbd>−</Kbd></dt><dd>{labels.helpZoom}</dd></div>
            <div><dt><Kbd>0</Kbd></dt><dd>{labels.helpFit}</dd></div>
            <div><dt><Kbd>1</Kbd></dt><dd>{labels.helpActual}</dd></div>
            <div><dt><Kbd>R</Kbd></dt><dd>{labels.helpRotate}</dd></div>
            <div><dt><Kbd>/</Kbd> <Kbd>Ctrl</Kbd>+<Kbd>F</Kbd></dt><dd>{labels.helpSearch}</dd></div>
            <div><dt><Kbd>F</Kbd></dt><dd>{labels.helpFullscreen}</dd></div>
            <div><dt><Kbd>I</Kbd></dt><dd>{labels.helpDetails}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}

function errorMessage(labels: FileViewerLabels, error: FileViewerError, maxRemoteBytes: number, source: FileViewerSource): string {
  switch (error.code) {
    case 'cors': return labels.errorCors;
    case 'auth': return labels.errorAuth;
    case 'not_found': return labels.errorNotFound;
    case 'expired': return labels.errorExpired;
    case 'http': return fill(labels.errorHttp, { status: error.status ?? '' });
    case 'too_large': {
      const size = source.kind === 'local' ? source.file.size : source.kind === 'remote' ? source.size ?? 0 : source.blob.size;
      const limit = source.kind === 'local' ? DEFAULT_MAX_LOCAL_BYTES : maxRemoteBytes;
      return fill(labels.errorTooLarge, { size: formatBytes(size || Number(error.message.split(' ')[0]) || 0), limit: formatBytes(limit) });
    }
    case 'unsupported': return labels.errorUnsupported;
    case 'decode': return labels.errorDecode;
    case 'aborted': return labels.errorAborted;
    default: return labels.errorNetwork;
  }
}

function formatWhen(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function detectFamilyOf(item: FileViewerSource): DetectedFile['family'] {
  const name = displayName(item);
  const mime = item.kind === 'local' ? item.file.type : item.kind === 'blob' ? (item.mime ?? item.blob.type) : item.mime;
  return detect({ name, mime, size: 0, head: new Uint8Array(0) }).family;
}
